import { FC, useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import Button from 'components/global/Button/Primary';
import Icon from 'components/global/Icon/Primary';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { ViaCapElementLarge } from './via-cap-element-large';
import { ViaCapElementSmall } from './via-cap-element-small';
import MuxPlayerAutoPause from 'components/reusable/MuxPlayer/mux-player';

import {
  TypeLabel,
  TypeDisclaimer,
  TypeHeadingTertiary,
} from 'components/library/Typography';
import {
  ViaCapSection,
  ViaCapBackgroundVideo,
  ViaCapVideoControl,
  ViaCapContainer,
  ViaCapContent,
  ViaCapNote,
  ViaCapBottomNote,
  NoteTitle,
  NotePerf,
  NotePerfArrow,
  NoteCount,
  TypeLabelLetter,
} from './via-cap.styled';

const notePerfArrowVariants = {
  initial: { transform: 'translateY(30px)', opacity: 0 },
  animate: {
    transform: 'translateY(0px)',
    opacity: 1,
    transition: {
      duration: 1,
    },
  },
};

interface ViaCapProps {
  eyebrow: string;
  description: string;
  background: { src: string; alt: string };
  backgroundVideo?: {
    desktop: { playbackId: string; devPlaybackId: string };
    mobile: { playbackId: string; devPlaybackId: string };
  };
  note: {
    title: string;
    pill: string;
    performance: number;
  };
  footnote: string;
  capsule: {
    media: {
      src: string;
      alt: string;
      width: number;
      height: number;
      type?: string;
    };
    top: {
      title: string;
      body: string;
    };
    center?: {};
    bottom: {
      title: string;
      body: string;
    };
  };
}

const ViaCap: FC<ViaCapProps> = ({
  eyebrow,
  description,
  background,
  backgroundVideo,
  note,
  footnote,
  capsule,
}) => {
  const isSmall = useClientSideMediaQuery(960);
  const [textRef, textRefInView] = useInView();
  const [shuffledText, setShuffledText] = useState<string>(eyebrow);
  const [count, setCount] = useState<number>(0);
  const [isMotionPaused, setIsMotionPaused] = useState<boolean>(false);
  const reducedMotion = useClientSideMediaQuery(
    '(prefers-reduced-motion: reduce)',
  );
  const backgroundVideoRef = useRef<HTMLDivElement>(null);
  const motionStopped = isMotionPaused || reducedMotion;

  const toggleMotion = () => {
    if (isMotionPaused) {
      // A user-gesture play() starts the video even when the browser blocked autoplay.
      const player = backgroundVideoRef.current?.querySelector('mux-player');
      (player as HTMLMediaElement | null)?.play()?.catch(() => undefined);
    }
    setIsMotionPaused((paused) => !paused);
  };

  let duration: number = 50;
  let framesMax: number = 7;

  const shuffleArr = (arr: string[]) => {
    return arr.reduce(
      ([a, b]) => (
        b.push(...a.splice((Math.random() * a.length) | 0, 1)), [a, b]
      ),
      [[...arr], []],
    )[1];
  };

  const shuffle = (text: string) => {
    let charArr = text.split('');
    let frame = 0;

    // shuffle at given speed
    let inter = setInterval(() => {
      if (frame < framesMax) {
        let charArrShuff = shuffleArr(charArr);
        setShuffledText(charArrShuff.join(''));
        frame++;
      } else {
        clearInterval(inter);
        setShuffledText(eyebrow);
      }
    }, duration);
  };

  const counter = () => {
    let start: number = 5;
    let end: number = note.performance;

    if (parseFloat(start.toFixed(2)) === end) return;

    let timer = setInterval(() => {
      start += 1;

      setCount(parseFloat(start.toFixed(2)));

      if (parseFloat(start.toFixed(2)) === end) {
        clearInterval(timer);
      }
    }, 100);
  };

  useEffect(() => {
    if (textRefInView) {
      shuffle(shuffledText);
      counter();
    }
  }, [textRefInView]);

  const backgroundVideoMedia = isSmall
    ? backgroundVideo?.mobile
    : backgroundVideo?.desktop;
  const backgroundPlaybackId =
    process.env.NEXT_PUBLIC_STAGING === 'true'
      ? backgroundVideoMedia?.devPlaybackId
      : backgroundVideoMedia?.playbackId;

  return (
    <ViaCapSection style={{ backgroundImage: `url(${background.src})` }}>
      {backgroundVideo && (
        <ViaCapBackgroundVideo aria-hidden='true' ref={backgroundVideoRef}>
          <MuxPlayerAutoPause
            playbackId={backgroundPlaybackId}
            poster={background.src}
            height='100%'
            width='100%'
            muted
            autoPlay={!reducedMotion}
            paused={motionStopped}
            loop
            playsInline
          />
        </ViaCapBackgroundVideo>
      )}
      <ViaCapVideoControl>
        <Button
          glass
          size='sm'
          onClick={toggleMotion}
          aria-label={isMotionPaused ? 'Play animations' : 'Pause animations'}
        >
          <Icon name={isMotionPaused ? 'play' : 'pause'} size='sm' />
        </Button>
      </ViaCapVideoControl>
      <ViaCapContainer>
        <ViaCapContent ref={textRef}>
          <TypeLabel color='inverse-primary'>
            ●{' '}
            {shuffledText.split('').map((text, index) => (
              <TypeLabelLetter
                key={index}
                style={{
                  opacity: textRefInView ? 1 : 0,
                  transitionDelay: `${index * 0.05}s`,
                }}
              >
                {text}
              </TypeLabelLetter>
            ))}
          </TypeLabel>
          <TypeHeadingTertiary color='inverse-primary'>
            {description}
          </TypeHeadingTertiary>
          {!isSmall && (
            <>
              <ViaCapNote>
                <NoteTitle data-pill={note.pill}>{note.title}</NoteTitle>
                <NotePerf>
                  <NotePerfArrow
                    variants={notePerfArrowVariants}
                    initial='initial'
                    whileInView='animate'
                  >
                    ↑
                  </NotePerfArrow>
                  <NoteCount>{count}</NoteCount>
                  <span>x</span>
                </NotePerf>
              </ViaCapNote>
              <TypeDisclaimer color='inverse-primary'>
                {footnote}
              </TypeDisclaimer>
            </>
          )}
        </ViaCapContent>

        {isSmall ? (
          <ViaCapElementSmall capsule={capsule} paused={motionStopped} />
        ) : (
          <ViaCapElementLarge capsule={capsule} paused={motionStopped} />
        )}

        {isSmall && (
          <ViaCapBottomNote>
            <ViaCapNote>
              <NoteTitle data-pill={note.pill}>{note.title}</NoteTitle>
              <NotePerf>
                <NotePerfArrow
                  variants={notePerfArrowVariants}
                  initial='initial'
                  whileInView='animate'
                >
                  ↑
                </NotePerfArrow>
                <NoteCount>{count}</NoteCount>
                <span>x</span>
              </NotePerf>
            </ViaCapNote>
            <TypeDisclaimer color='inverse-primary'>{footnote}</TypeDisclaimer>
          </ViaCapBottomNote>
        )}
      </ViaCapContainer>
    </ViaCapSection>
  );
};

export default ViaCap;
